<template>
  <div 
    class="custom-line-chart"
    ref="lineChart"></div>
</template>

<script>
import _ from 'lodash';
import { SIDEBAR_CALLAPSED } from '@/common/events.types';
import * as lineChartService from '@/services/linechartService';
import * as d3 from 'd3';
import { UNIT_LABELS, UNIT_SYSTEM } from '../../../constants/UnitConsts';

export default {
  name: 'LineChart',
  props: [
    'data',
    'duration',
    'height',
    'x',
    'y',
    'xUnit',
    'yUnit',
    'yAxisTitle',
    'xAxisTitle',
    'yTicks',
    'displayBrushes',
    'onBrushCreation',
    'segments',
    'triggerMoreDetails',
    'xMaxSeconds',
    'detailValuesMiddleZoom',
    'detailValuesHighZoom',
    'chartDataMediumPrecise',
    'chartDataHighPrecise',
    'chartDataHighestPrecise',
    'playerInfo',
    'mainLineColor',
    'chartMetricsType',
    'zoomSelection',
    'updateZoomSelection',
    'dataPointsParamName',
    'presentationView'
  ],
  data () {
    return {
      animationDuration: 300,
      timeIntervalMediumPrecise: 30,
      timeIntervalHighPrecise: 15,
      timeIntervalHighestPrecise: 5,
      mediumPreciseScope: 30,
      highPreciseScope: 10,
      highestPreciseScope: 2,
      chart: null,
      defaultXScale: null,
      xScale: null,
      yScale: null,
      line: null,
      lines: null,
      dots: null,
      svg: null,
      main: null,
      clipPath: null,
      tooltip: null,
      focus: null,
      clipPathRect: null,
      xAxis: null,
      width: 0,
      yAxisLabel: '',
      xTicks: [],
      tooltipWidth: 200,
      tooltipHeight: 80,
      margin: { top: 50, right: 50, bottom: 50, left: 50 },
      detailMediumDots: {},
      detailHighDots: {},
      xTicksMediumZoom: [],
      xTicksHighZoom: [],
      numberOfXAxis: 10,
      zoomedSelection: [],
      defaultXAxis: null,
      liveSelection: null
    }
  },
  computed: {
    xAxisSteps () {
      return parseInt(Math.floor(this.duration / 60)) / 10;
    },
    xMaxValue () {
      return Math.floor(this.xMaxSeconds / 60);
    },
    xMaxDecimal () {
      return this.xMaxSeconds / 60;
    },
    chartHeight () {
      return this.height * 7 / 8;
    },
    zoomPreviewHeight () {
      return this.height * 1 / 8;
    },
    middleZoomxMaxValue () {
      return this.xMaxSeconds;
    },
    xAxisStepsMiddleZoom () {
      return parseInt(this.duration) / this.timeIntervalMediumPrecise;
    },
    locationHref () {
      return `${window.location.origin}${this.$router.currentRoute.fullPath}`;
    },
    dataPointsParameterName () {
      return this.dataPointsParamName || 'dataPoints';
    }
  },
  mounted () {
    this.initChart();

    window.addEventListener('resize', () => {
      this.updateChartWidth();
      lineChartService.updateSegments(this.segments, this.main, this.zoomedSelection, this.xMaxDecimal, this.width, this.chartHeight);
    });

    this.$eventHub.$on(SIDEBAR_CALLAPSED, () => {
      const context = this;
      setTimeout(function() {
        context.updateChartWidth();
        lineChartService.updateSegments(context.segments, context.main, context.zoomedSelection, context.xMaxDecimal, context.width, context.chartHeight);
      }, 200);
    });
    // event fired on canceling create Segment 
    this.$eventHub.$on('remove-brushes-on-calendar-activity-chart', () => {
      if (this.gBrushes != undefined) {
        this.gBrushes.selectAll('.brush').remove();
        this.newBrush();
        lineChartService.drawBrushes(this.gBrushes, this.brushes);
      }
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.initChart)
  },
  watch: {
    segments (newValues) {
      if(this.displayBrushes && window.innerWidth > 768 && this.gBrushes) { 
        this.gBrushes.selectAll('.brush').remove();
        this.newBrush();
        lineChartService.drawBrushes(this.gBrushes, this.brushes);
      }
      
      if(newValues && newValues.push) {
        lineChartService.drawSegments(this.segments, this.gSegments, this.zoomedSelection, this.xMaxDecimal, this.width, this.chartHeight, this.locationHref);
      }
    },
    chartDataMediumPrecise (newValue) {
      const path = this.svg.select('#path-id-' + this.playerInfo.playerId + '_medium-precise-path');

      if(!path.empty()) {
        const data = Object.keys(newValue)
          .map(function(key) {
            return { y: newValue[key], x: parseInt(key) };
          });


        const lowestHighestY = this.calculateHighestLowestYValue(newValue, this.selection);
        this.lowestYPoint = lowestHighestY[0];
        this.highestYPoint = lowestHighestY[1];
        this.yScale.domain([this.lowestYPoint, this.highestYPoint === this.y.max ? this.highestYPoint : this.highestYPoint]);
        this.main.select(".y.axis")
          .transition()
          .duration(this.animationDuration)  
          .ease(d3.easeLinear)
          .call(this.yAxis);
        lineChartService.updateGridlines(this.main, this.xScaleMiddleZoom, this.xTicksMediumZoom, this.chartHeight, this.yScale, this.yTicks, this.width);

        path
          .datum(data)
          .transition()
          .duration(this.animationDuration)  
          .ease(d3.easeLinear)
          .attr('d', this.mediumZoomline);
      }
    },
    chartDataHighPrecise (newValue) {
      const path = this.svg.select('#path-id-' + this.playerInfo.playerId + '_high-precise-path');

      if(!path.empty()) {
        const data = Object.keys(newValue)
          .map(function(key) {
            return { y: newValue[key], x: parseInt(key) };
          });

        const lowestHighestY = this.calculateHighestLowestYValue(newValue, this.selection);
        this.lowestYPoint = lowestHighestY[0];
        this.highestYPoint = lowestHighestY[1];
        this.yScale.domain([this.lowestYPoint, this.highestYPoint === this.y.max ? this.highestYPoint : this.highestYPoint]);
        this.main.select(".y.axis")
          .transition()
          .duration(this.animationDuration)  
          .ease(d3.easeLinear)
          .call(this.yAxis);
        lineChartService.updateGridlines(this.main, this.xScaleMiddleZoom, this.xTicksMediumZoom, this.chartHeight, this.yScale, this.yTicks, this.width);

        path
          .datum(data)
          .transition()
          .duration(this.animationDuration)  
          .ease(d3.easeLinear)
          .attr('d', this.mediumZoomline);
      }
    },
    chartDataHighestPrecise (newValue) {
      const path = this.svg.select('#path-id-' + this.playerInfo.playerId + '_highest-precise-path');

      if(!path.empty()) {
        const data = Object.keys(newValue)
          .map(function(key) {
            return { y: newValue[key], x: parseInt(key) };
          });

        const lowestHighestY = this.calculateHighestLowestYValue(newValue, this.selection);
        this.lowestYPoint = lowestHighestY[0];
        this.highestYPoint = lowestHighestY[1];
        this.yScale.domain([this.lowestYPoint, this.highestYPoint === this.y.max ? this.highestYPoint : this.highestYPoint]);
        this.main.select(".y.axis")
          .transition()
          .duration(this.animationDuration)  
          .ease(d3.easeLinear)
          .call(this.yAxis);
        lineChartService.updateGridlines(this.main, this.xScaleMiddleZoom, this.xTicksMediumZoom, this.chartHeight, this.yScale, this.yTicks, this.width);

        path
          .datum(data)
          .transition()
          .duration(this.animationDuration)  
          .ease(d3.easeLinear)
          .attr('d', this.mediumZoomline);
      }
    },
    duration(){
      this.initChart();
    }
  },
  methods: {
    initChart () {
      if(this.svg) {
        this.svg.remove();
      }

      this.selection = _.clone(this.zoomSelection);

      const context = this;
      if (this.$refs.lineChart.parentElement) {
        this.width = this.$refs.lineChart.parentElement.offsetWidth - this.margin.right - 7;
      }
      this.defaultXScale = d3
        .scaleLinear()
        .domain([this.x.min, this.xMaxDecimal]) // input
        .range([0, this.width]); // output
      
      this.xScale = this.defaultXScale;

      this.xScaleMiddleZoom = d3
        .scaleLinear()
        .domain([this.x.min, this.middleZoomxMaxValue])
        .range([0, this.width]);

      this.xScalePreviewChart = d3
        .scaleLinear()
        .domain([this.x.min, this.xMaxDecimal]) // input
        .range([0, this.width]); // output
      
      this.yScalePreviewChart = d3
        .scaleLinear()
        .domain([this.y.min, this.y.max]) // input
        .range([this.zoomPreviewHeight, 0]); // output

      this.yScale = d3
        .scaleLinear()
        .domain([this.y.min, this.y.max]) // input
        .range([this.chartHeight, 0]); // output

      // line generator
      this.line = d3
        .line()
        .x(function(d) {
          return context.xScale(d.x);
        }) // set the x values for the line generator
        .y(function(d) {
          return context.yScale(d.y);
        }) // set the y values for the line generator
        .curve(d3.curveMonotoneX) // apply smoothing to the line
        .defined(function(d) {
          return d.y !== null;
        })
      // if presentation
      this.mediumZoomline = d3
        .line()
        .x(function(d) {
          return context.xScaleMiddleZoom(d.x);
        }) // set the x values for the line generator
        .y(function(d) {
          return context.yScale(d.y);
        }) // set the y values for the line generator
        .curve(d3.curveMonotoneX) // apply smoothing to the line
        .defined(function(d) {
          return d.y !== null;
        })
      // if presentation 
      if (!this.presentationView) {
        // line zoom preview generator
        this.previewLine = d3
          .line()
          .x(function(d) {
            return context.xScalePreviewChart(d.x);
          }) // set the x values for the line generator
          .y(function(d) {
            return context.yScalePreviewChart(d.y);
          }) // set the y values for the line generator
          .curve(d3.curveMonotoneX) // apply smoothing to the line
          .defined(function(d) {
            return d.y !== null;
          })
      }
      

      let ld = lineChartService.generateLinesAndDots(this.data, this.dataPointsParameterName);
      this.lines = ld.lines;
      this.dots = ld.dots;

      // Add the SVG to the page
      this.svg = d3
        .select(this.$refs.lineChart)
        .append("svg")
        .attr("width", this.width + this.margin.left + this.margin.right)
        .attr("height", this.height + this.margin.top + this.margin.bottom)
        .attr("id", "d3-js")

      this.main = this.svg
        .append("g")
        .attr("transform", "translate(" + this.margin.left + "," + (this.margin.top + this.zoomPreviewHeight) + ")");

      // Add defs to chart
      this.defs = this.main.append('defs');

      this.clipPath = this.defs
        .append('clipPath')
        .attr('id', 'clip')

      this.clipPathRect = this.clipPath
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', this.width)
        .attr('height', this.chartHeight);

      this.clipXaxis = this.defs
        .append('clipPath')
        .attr('id', 'clipXaxis');

      this.clipXaxisRect = this.clipXaxis
        .append('rect')
        .attr('x', -10)
        .attr('y', 0)
        .attr('width', this.width + 30)
        .attr('height', 110);

      this.clipYaxis = this.defs
        .append('clipPath')
        .attr('id', 'clipYaxis');
        
      this.clipYaxisRect = this.clipYaxis
        .append('rect')
        .attr('x', -40)
        .attr('y', -10)
        .attr('height', context.chartHeight + 20)
        .attr('width', 50);

      this.xGridPath = this.defs
        .append('clipPath')
        .attr('id', 'xGridPath');

      this.xGridPathRect = this.xGridPath.append('rect')
        .attr('x', 0)
        .attr('y', -context.chartHeight)
        .attr('height', context.chartHeight)
        .attr('width', context.width)

      for(let i = 0; i <= this.xAxisSteps; i++) {
        this.xTicks.push(parseInt(i * 10));
      }

      for(let i = 0; i <= this.xAxisStepsMiddleZoom; i+= this.highPreciseScope) {
        this.xTicksHighZoom.push(i);
      }

      for(let i = 0; i <= this.xMaxSeconds; i+= this.mediumPreciseScope) {
        this.xTicksMediumZoom.push(i);
      }

      this.defaultXAxis = d3.axisBottom(this.xScale).tickValues(this.xTicks);

      if(window.innerWidth < 768) {
        this.xAxisMiddleZoom = d3
          .axisBottom(this.xScale)
          .tickValues(this.xTicks);

        this.xAxisHighZoom = d3
          .axisBottom(this.xScale)
          .tickValues(this.xTicks);
      } else {
        this.xAxisMiddleZoom = d3
          .axisBottom(this.xScaleMiddleZoom)
          .tickValues(this.xTicksMediumZoom)
          .tickFormat(this.formatZoomedInValues);

        this.xAxisHighZoom = d3
          .axisBottom(this.xScaleMiddleZoom)
          .tickValues(this.xTicksHighZoom)
          .tickFormat(this.formatZoomedInValues);
      }

      // Call the x axis in a group tag
      this.xAxis = this.defaultXAxis;
      this.yAxis = d3.axisLeft(this.yScale).tickValues(this.yTicks);

      this.xAxisChartTicks = this.main
        .append("g")
        .attr("class", "x axis")
        .attr('clip-path', 'url(' + this.locationHref + '#clipXaxis)')
        .attr("transform", "translate(0," + this.chartHeight + ")")

      if(this.width < 500) {
        this.xAxisChartTicks
          .selectAll("text")	
          .style("text-anchor", "end")
          .attr("dx", "-.8em")
          .attr("dy", ".15em")
          .attr("transform", "rotate(-90)");
      }

      // Draw Axis
      // Call the y axis in a group tag
      this.yAxisG = this.main
        .append("g")
        .attr("class", "y axis")
        .call(this.yAxis)

      this.yAxisLabelWrapper = this.main  
        .append("g")
        .attr("fill", "#5E5E62")

      this.main.select(".x.axis")
          .transition()
          .duration(this.animationDuration)  
          .ease(d3.easeLinear)
          .call(this.defaultXAxis);

      // Y axix label
      this.yAxisLabel = this.yAxisLabelWrapper
        .append("text")
        .attr("transform", "rotate(-90)")
        .attr("class", "axis-label axis-label-y")
        .attr("x", -this.chartHeight / 2)
        .attr("y", -50)
        .attr("dy", ".71em")
        .style("text-anchor", "middle")
        .text(this.yAxisTitle); 

      // X axix label
      this.xAxisLabelWrapper = this.main
        .append("g")
        .attr("fill", "#5E5E62")

      this.xAxisLabel = this.xAxisLabelWrapper
        .append("text")
        .attr("transform", "translate(0," + this.chartHeight + ")")
        .attr("class", "axis-label axis-label-x")
        .attr("x", this.width / 2)
        .attr("y", 50)
        .style("text-anchor", "middle")
        .text(this.xAxisTitle);

      // Add a X gridlines
      this.main
        .append("g")
        .attr("class", "grid grid-x")
        .attr("transform", "translate(0," + this.chartHeight + ")")
        .attr('clip-path', 'url(' + this.locationHref + '#xGridPath)')
        .call(
          d3
            .axisBottom(this.xScale)
            .ticks(this.xTicks.length)
            .tickValues(this.xTicks)
            .tickSize(-this.chartHeight)
            .tickFormat("")
        ); // Create an axis component with d3.axisBottom

      // Add a Y gridlines
      this.yGridlines = this.main
        .append("g")
        .attr("class", "grid grid-y")
        .call(
          d3
            .axisLeft(this.yScale)
            .ticks(this.yTicks.length)
            .tickValues(this.yTicks)
            .tickSize(-this.width)
            .tickFormat("")
        ); // Create an axis component with d3.axisBottom

      this.pathGroup = this.main
        .append('g')
        .attr('class', 'path-group');

      if(this.xMaxSeconds > 120) {
        this.zoomContext = this.svg
          .append("g")
          .attr("class", "zoom-context")
          .attr("transform", "translate(" + this.margin.left + "," + 20 + ")");

        this.zoomContextPathGroup = this.zoomContext
          .append('g')
          .attr('class', 'path-group');

        this.zoomBrush = d3.brushX()
          .extent([[0, 0], [this.width, this.zoomPreviewHeight]])
          .on("brush", this.onZoomBrushing)
          .on('end', this.onZoomBrushEnd);

        // add defs to zoom context chart
        this.previewDefs = this.zoomContext.append('defs');

        this.clipPreviewPath = this.previewDefs
          .append('clipPath')
          .attr('id', 'clipPreview');

        this.clipPreviewRect = this.clipPreviewPath
          .append('rect')
          .attr('x', 0)
          .attr('y', 0)
          .attr('width', this.width)
          .attr('height', this.zoomPreviewHeight);

        this.zoomContext
          .append("g")
          .attr("class", "axis axis--x")
          .attr("transform", "translate(0," + this.zoomPreviewHeight + ")")
          .call(this.xAxis);
      }

      // Append lines
      lineChartService.appendLinesToChart(this.lines, this.pathGroup, this.line, this.previewLine, this.zoomContextPathGroup, this.locationHref);

      // Generate Focus
      this.focus = lineChartService.appendAndGenerateFocus(this.main, this.width, this.chartHeight, this.locationHref);

      if(this.xMaxSeconds > 120 && !this.presentationView) {
        this.zBrush = this.zoomContext.append("g")
          .attr("class", "brush zoom-brush")
          .call(this.zoomBrush);

        this.handle = this.zBrush.selectAll(".handle--custom")
          .data([{type: "w"}, {type: "e"}])
          .enter().append("path")
            .attr("class", "handle--custom")
            .attr("stroke-width", 1.5)
            .attr("stroke", "#8C8C97")
            .attr("cursor", "ew-resize")
            .attr("d", this.brushResizePath);

        if(this.zoomSelection && this.zoomSelection.length) {
          const selectionInWidth = [];
          selectionInWidth[0] = (this.zoomSelection[0] / this.xMaxSeconds) * this.width;
          selectionInWidth[1] = (this.zoomSelection[1] / this.xMaxSeconds) * this.width;

          this.zBrush.call(this.zoomBrush.move, selectionInWidth);
        } else {
          this.zBrush.call(this.zoomBrush.move, this.xScale.range());
        }
      }
      

      // Append brushes
      if(this.displayBrushes) {
        this.generateBrushes();
      } 

      // Display brushes
      if(this.displayBrushes && window.innerWidth > 768) {
        this.newBrush();
        lineChartService.drawBrushes(this.gBrushes, this.brushes);
      }

      this.gSegments = this.main.insert('g', '.brushes').attr('class', 'chart-segments');

      if(this.segments && this.segments.length) {
        lineChartService.drawSegments(this.segments, this.gSegments, this.zoomedSelection, this.xMaxDecimal, this.width, this.chartHeight, this.locationHref);
      }

      // Generate Tooltip
      if (!this.presentationView) {
        this.tooltip = lineChartService.appendAndGenerateTooltip(this.main, this.tooltipWidth, this.tooltipHeight, this.locationHref);
      }

      let zoomIconZoomWidth = this.width;

      if (!this.presentationView) {
        if(this.zoomSelection[1]) {
          const leftPart = (this.zoomSelection[0] / this.xMaxSeconds) * this.width;
          zoomIconZoomWidth = leftPart + (this.zoomSelection[1] / this.xMaxSeconds) * this.width;
        }
      }

      this.zoomIconWrapper = this.zoomContext.append('g')
        .attr('class', 'zoom-icon-wrapper')
        .attr('width', 24)
        .attr('height', 24)
        .attr('fill', '#000')
        .attr('transform', 'translate(' + (zoomIconZoomWidth / 2 - 12) + ', ' + ((this.zoomPreviewHeight / 2) - 12) + ')')
        .on('click', function() {
          if((context.zoomedSelection[1] - context.zoomedSelection[0]) < context.xMaxDecimal) {
            context.zBrush.call(context.zoomBrush.move, context.xScale.range());
          } else {
            context.zBrush.call(context.zoomBrush.move, [0, Math.floor(context.width / 3)]);
          }
        });

      const svgZoomWrapper = this.zoomIconWrapper.append('svg')
        .attr('width', 24)
        .attr('height', 24)
        .attr('viewBox', '0 0 24 24')
        .attr('stroke', '#fff')
        .attr('stroke-width', 2)
        .attr('stroke-linecap', 'round')
        .attr('stroke-linejoin', 'round')
        .attr('class', 'icon-zoom chart-zoom');

      const svgZoomGroup = 
        svgZoomWrapper.append('g')
          .attr('fill', 'transparent')
          .attr('width', 24)
          .attr('height', 24)
          .attr('cursor', 'pointer')

      svgZoomGroup.append('circle')
        .attr('cx', 11)
        .attr('cy', 11)
        .attr('r', 8);

      svgZoomGroup.append('line')
        .attr('x1', 21)
        .attr('y1', 21)
        .attr('x2', 16.65)
        .attr('y2', 16.65);

      svgZoomGroup.append('line')
        .attr('x1', 8)
        .attr('y1', 11)
        .attr('x2', 14)
        .attr('y2', 11);

      this.plusPartOfZoomIcon = svgZoomGroup.append('line')
        .attr('x1', 11)
        .attr('y1', 8)
        .attr('x2', 11)
        .attr('y2', 14);

      this.yAxisG
        .attr('clip-path', 'url(' + this.locationHref + '#clipYaxis)')

      this.yGridlines
        .attr('clip-path', 'url(' + this.locationHref + '#clip)');

      if(!this.displayBrushes && !this.presentationView) {
        this.main
          .append('g')
          .attr('class', 'main-search-scope')
          .append('rect')
          .attr('width', this.width)
          .attr('height', this.chartHeight)
          .attr("fill", 'transparent')
          .on('mousemove', function() {
            context.mouseMoveOnChart(this);
          })
          .on("mouseout", function() {
            context.handleMouseOut(this);
          });
      }
    },
    onZoomBrushing () {
      const context = this;
      const s = d3.event.selection;
      const sourceEventType = d3.event.sourceEvent ? d3.event.sourceEvent.type : 'no-event';
      const zoomBrushEl = this.zoomContext.selectAll('.brush.zoom-brush .selection');
      let zoomBrushWidth = 0;
      
      if(zoomBrushEl) {
        zoomBrushWidth = zoomBrushEl.node().getBoundingClientRect().width;
      }

      if (s == null) {
        this.handle.attr("display", "none");
      } else {
        this.handle.attr("display", null).attr("transform", function(d, i) { 
          return "translate(" + s[i] + "," + -context.zoomPreviewHeight + ")"; 
        });
      }

      const selection = d3.event.selection.map(function(i) {
        return context.xScalePreviewChart.invert(i) ;
      });

      if(sourceEventType === 'mousemove' || sourceEventType === 'click' || sourceEventType === 'no-event') {
        if((selection[1] - selection[0]) < 2) {
          const selectionInWidth = [];

          selectionInWidth[0] = this.liveSelection[0];
          selectionInWidth[1] = this.liveSelection[1];

          if((selection[0] * 60 + 120) > this.xMaxSeconds) {
            selectionInWidth[1] = this.width;
            selectionInWidth[0] = ((this.xMaxSeconds - 120) / this.xMaxSeconds) * this.width;
          }

          this.zBrush.call(this.zoomBrush.move, selectionInWidth);
          return;
        }

        this.liveSelection = _.clone(s);
        
        if(selection[1] - selection[0] > 0) {
          let lowestYPoint = Infinity;
          let highestYPoint = 0;

          if (d3.event.sourceEvent && d3.event.sourceEvent.type === "zoom") return; // ignore brush-by-zoom

          if(this.plusPartOfZoomIcon) {
            if(selection[1] - selection[0] < this.xMaxDecimal) {
              this.plusPartOfZoomIcon.attr('display', 'none');
            } else {
              this.plusPartOfZoomIcon.attr('display', null);
            }
          }
          
          this.zoomedSelection = [selection[0], selection[1]];
          const zoomedSelectionWidthSelectionRight = (selection[1] / this.xMaxDecimal) * this.width;
          const zoomedSelectionWidthSelectionLeft = (selection[0] / this.xMaxDecimal) * this.width;

          if(this.zoomIconWrapper) {
            if(zoomBrushWidth > 35) {
              this.zoomIconWrapper
                .attr('display', null)
            } else {
              this.zoomIconWrapper.attr('display', 'none');
            }

            this.zoomIconWrapper
              .attr('transform', 'translate(' + ((zoomedSelectionWidthSelectionLeft + zoomedSelectionWidthSelectionRight) / 2 - 12) + ', ' + ((this.zoomPreviewHeight / 2) - 12) + ')');
          }

          if(d3.event.selection && this.xAxis) {
            const selectionScope = selection[1] - selection[0];

            if(selectionScope < this.mediumPreciseScope && this.triggerMoreDetails) {
              const domain = [selection[0] * 60, selection[1] * 60];
              this.xScaleMiddleZoom.domain(domain);
              this.xScale.domain(selection);
              this.chartMode = 'ZOOMED_IN_MEDIUM';

              let portionOfTheChart = selectionScope * 60 / context.xMaxSeconds;
              let numberOfDisplayedTicks = portionOfTheChart * context.xTicksMediumZoom.length;
              let spaceForTick = context.width / numberOfDisplayedTicks;

              if(spaceForTick < 37) {
                let i = 1;

                while(spaceForTick < 37) {
                  numberOfDisplayedTicks = portionOfTheChart * (context.xMaxSeconds / (i *60));
                  spaceForTick = context.width / numberOfDisplayedTicks;
                  i++;
                }

                const newTicksMediumsZoom = [];
                const ticksLength = Math.ceil(context.xMaxSeconds / (i * 60));

                for(let j = 0; j < ticksLength; j++) {
                  newTicksMediumsZoom.push(j * i * 60);
                }

                if(newTicksMediumsZoom[newTicksMediumsZoom.length - 1] > context.xMaxSeconds) {
                  newTicksMediumsZoom[newTicksMediumsZoom.length - 1] = context.xMaxSeconds;
                }

                context.xAxisMiddleZoom = d3
                  .axisBottom(context.xScaleMiddleZoom)
                  .tickValues(newTicksMediumsZoom)
                  .tickFormat(context.formatZoomedInValues);
              } else {
                context.xAxisMiddleZoom = d3
                  .axisBottom(context.xScaleMiddleZoom)
                  .tickValues(context.xTicksMediumZoom)
                  .tickFormat(context.formatZoomedInValues);
              }

              if(window.innerWidth > 768) {
                this.main.select(".x.axis")
                  .transition()
                  .duration(this.animationDuration)  
                  .ease(d3.easeLinear)
                  .call(this.xAxisMiddleZoom);

              } else {
                this.main.select(".x.axis")
                  .transition()
                  .duration(this.animationDuration)  
                  .ease(d3.easeLinear)
                  .call(this.xScale);
              }

              const keys = Object.keys(this.chartDataMediumPrecise);
              
              if(selectionScope > 15) {
                if(keys.length) {
                  const lowestHighestPoints = this.calculateHighestLowestYValue(this.chartDataMediumPrecise, this.selection);
                  lowestYPoint = lowestHighestPoints[0];
                  highestYPoint = lowestHighestPoints[1];
                }

              } else {
                if(keys.length) {
                  const lowestHighestPoints = this.calculateHighestLowestYValue(this.chartDataMediumPrecise, this.selection);
                  lowestYPoint = lowestHighestPoints[0];
                  highestYPoint = lowestHighestPoints[1];
                }
              }

              if(lowestYPoint === Infinity || highestYPoint === 0) {
                lowestYPoint = this.yTicks[0];
                highestYPoint = this.yTicks[this.yTicks.length - 1];
              }

              this.yScale.domain([lowestYPoint, highestYPoint === this.y.max ? highestYPoint : highestYPoint]);
              this.main.select(".y.axis")
                .transition()
                .duration(this.animationDuration)  
                .ease(d3.easeLinear)
                .call(this.yAxis);

              lineChartService.updateSegments(this.segments, this.main, this.zoomedSelection, this.xMaxDecimal, this.width, this.chartHeight);
              lineChartService.updateGridlines(this.main, this.xScaleMiddleZoom, this.xTicksMediumZoom, this.chartHeight, this.yScale, this.yTicks, this.width);

              if(selectionScope <= 5) {
                this.main
                  .selectAll(".line.path.highest-precise-path")
                  .transition()
                  .duration(this.animationDuration)  
                  .ease(d3.easeLinear)
                  .attr("d", this.mediumZoomline);

                this.main
                  .selectAll('.line.path.default-path')
                  .attr("display", 'none');

                this.main
                  .selectAll(".line.path.medium-precise-path")
                  .attr('display', 'none');


                this.main
                  .selectAll(".line.path.high-precise-path")
                  .attr('display', 'none');

                this.main
                  .selectAll(".line.path.highest-precise-path")
                  .attr('display', null);
                
              } else if (selectionScope <= 15) {
                this.main
                  .selectAll(".line.path.high-precise-path")
                  .transition()
                  .duration(this.animationDuration)  
                  .ease(d3.easeLinear)
                  .attr("d", this.mediumZoomline);

                this.main
                  .selectAll('.line.path.default-path')
                  .attr("display", 'none');

                this.main
                  .selectAll(".line.path.medium-precise-path")
                  .attr('display', 'none');

                this.main
                  .selectAll(".line.path.highest-precise-path")
                  .attr('display', 'none');

                this.main
                  .selectAll(".line.path.high-precise-path")
                  .attr('display', null);

              } else if (selectionScope <= 30) {
                this.main
                  .selectAll(".line.path.medium-precise-path")
                  .transition()
                  .duration(this.animationDuration)  
                  .ease(d3.easeLinear)
                  .attr("d", this.mediumZoomline);

                this.main
                  .selectAll('.line.path.default-path')
                  .attr("display", 'none');

                this.main
                  .selectAll(".line.path.medium-precise-path")
                  .attr('display', null);

                this.main
                  .selectAll(".line.path.highest-precise-path")
                  .attr('display', 'none');

                this.main
                  .selectAll(".line.path.high-precise-path")
                  .attr('display', 'none');
              }


            } else {
              this.chartMode = 'DEFAULT';
              this.xScale.domain(selection);

              if(this.data && this.data.length && this.zoomedSelection && this.zoomedSelection.length) {
                if(this.zoomedSelection[0] !== 0 || this.zoomedSelection[1] !== this.xMaxDecimal) {
                  const chartDataPoints = this.data[0][this.dataPointsParameterName];

                  for(let i = 0; i < chartDataPoints.length; i++) {
                    if(chartDataPoints[i] > highestYPoint) {
                      highestYPoint = chartDataPoints[i];
                    }

                    if(chartDataPoints[i] < lowestYPoint) {
                      lowestYPoint = chartDataPoints[i];
                    }
                  }
                } else {
                  lowestYPoint = this.yTicks[0];
                  highestYPoint = this.yTicks[this.yTicks.length - 1];
                }
              } else {
                lowestYPoint = this.yTicks[0];
                highestYPoint = this.yTicks[this.yTicks.length - 1];
              }
              // support for dynamic x axis ticks (SD - speedDistance)
              let portionOfTheChartSD = (selectionScope * 60) / context.xMaxSeconds;
              let numberOfDisplayedTicksSD = portionOfTheChartSD * context.xTicksMediumZoom.length;
              let spaceForTickSD = context.width / numberOfDisplayedTicksSD;

              if(spaceForTickSD < 37) {
                let i = 1;

                while(spaceForTickSD < 37) {
                  numberOfDisplayedTicksSD = portionOfTheChartSD * (context.xMaxSeconds / (i *60));
                  spaceForTickSD = context.width / numberOfDisplayedTicksSD;
                  i++;
                }

                const newTicksMediumsZoom = [];
                const ticksLength = Math.ceil(context.xMaxSeconds / (i * 60));

                for(let j = 0; j < ticksLength; j++) {
                  newTicksMediumsZoom.push(j * i * 60);
                }

                if(newTicksMediumsZoom[newTicksMediumsZoom.length - 1] > context.xMaxSeconds) {
                  newTicksMediumsZoom[newTicksMediumsZoom.length - 1] = context.xMaxSeconds;
                }

                context.xAxisMiddleZoom = d3
                  .axisBottom(context.xScaleMiddleZoom)
                  .tickValues(newTicksMediumsZoom)
                  .tickFormat(context.formatZoomedInValues);
              } else {
                context.xAxisMiddleZoom = d3
                  .axisBottom(context.xScaleMiddleZoom)
                  .tickValues(context.xTicksMediumZoom)
                  .tickFormat(context.formatZoomedInValues);
              }

              const domain = [selection[0] * 60, selection[1] * 60];

              if (selectionScope <= 30) {
                this.xScaleMiddleZoom.domain(domain);
                this.main.select(".x.axis")
                .transition()
                .duration(this.animationDuration)  
                .ease(d3.easeLinear)
                .call(this.xAxisMiddleZoom);
                // update gridlines
                lineChartService.updateGridlines(this.main, this.xScaleMiddleZoom, this.xTicksMediumZoom, this.chartHeight, this.yScale, this.yTicks, this.width);

              } else {
                this.main.select(".x.axis")
                .transition()
                .duration(this.animationDuration)  
                .ease(d3.easeLinear)
                .call(this.defaultXAxis);

                lineChartService.updateGridlines(this.main, this.xScale, this.xTicks, this.chartHeight, this.yScale, this.yTicks, this.width);
              }
              // support for dynamic x-axis ticks end
              this.yScale.domain([lowestYPoint, highestYPoint === this.y.max ? highestYPoint : highestYPoint]);
              this.main.select(".y.axis")
                .transition()
                .duration(this.animationDuration)  
                .ease(d3.easeLinear)
                .call(this.yAxis);
              
              lineChartService.updateSegments(this.segments, this.main, this.zoomedSelection, this.xMaxDecimal, this.width, this.chartHeight);

              this.main
                .selectAll(".line.path.default-path")
                .transition()
                .duration(this.animationDuration)  
                .ease(d3.easeLinear)
                .attr("d", this.line);

              this.main
                .selectAll('.line.path.default-path')
                .attr("display", null);

              this.main
                .selectAll(".line.path.medium-precise-path")
                .attr('display', 'none');

              this.main
                .selectAll(".line.path.high-precise-path")
                .attr('display', 'none');

              this.main
                .selectAll(".line.path.highest-precise-path")
                .attr('display', 'none');

            }
          } else {
            this.zoomBrush.extent(this.selection);
            
            return false;
          }
        }
      }
    },
    onZoomBrushEnd () {
      const context = this;
      if (!this.presentationView) {
          if(d3.event.selection) {
          const selectionInMinutes = d3.event.selection.map(function(i) {
            return context.xScalePreviewChart.invert(i) ;
          });

          const selection = [];
          const startingPoint = selectionInMinutes[0] * 60;
          const endingPoint = selectionInMinutes[1] * 60;
          const selectionScope = selectionInMinutes[1] - selectionInMinutes[0];

          if(selectionScope > this.timeIntervalHighPrecise && selectionScope < this.timeIntervalMediumPrecise) {
            let reducerStartPoint = startingPoint % 30;
            let reducerEndingPoint = 30 - (endingPoint % 30);
            selection[0] = startingPoint - reducerStartPoint;
            selection[1] = endingPoint + reducerEndingPoint;
            this.selection = selection;

          } else if(selectionScope < 15) {
            let reducerStartPoint = startingPoint % 2;
            let reducerEndingPoint = 2 - (endingPoint % 2);
            selection[0] = startingPoint - reducerStartPoint;
            selection[1] = endingPoint + reducerEndingPoint;
            this.selection = selection;
          }

          if(selection.length) {
            if(selection[1] >= this.xMaxSeconds) { 
              selection[1] = this.xMaxSeconds;
            }

            if(this.triggerMoreDetails) {
              this.triggerMoreDetails({ 
                selection: selection,
                chartMetricsType: this.chartMetricsType
              });
            }
            
          } else {
            this.selection = [startingPoint, endingPoint];
          }
      
          this.updateZoomSelection(this.selection);
          
        } else {
          this.zBrush.call(this.zoomBrush.move, this.xScale.range());
        }
      }
    },
    brushResizePath (d) {
      let e = +(d.type == "e"),
          x = e ? 1 : -1,
          y = this.zoomPreviewHeight;
          
      return "M" + (.5 * x) + "," + y + "A6,6 0 0 " + e + " " + (6.5 * x) + "," + (y + 6) + "V" + (2 * y - 6) + "A6,6 0 0 " + e + " " + (.5 * x) + "," + (2 * y) + "Z" + "M" + (2.5 * x) + "," + (y + 8) + "V" + (2 * y - 8) + "M" + (4.5 * x) + "," + (y + 8) + "V" + (2 * y - 8);
    },
    calculateHighestLowestYValue (objData, selection) {
      let lowestYPoint = Infinity;
      let highestYPoint = 0;

      for(var point in objData) {
        if(point > parseInt(selection[0]) && point < parseInt(selection[1])) {
          if(objData[point] < lowestYPoint) {
            lowestYPoint = objData[point];
          }

          if(objData[point] > highestYPoint) {
            highestYPoint = objData[point];
          }
        }
      }

      const highestPossiblePoint = this.yTicks[this.yTicks.length - 1];
      const lowestPossiblePoint = this.yTicks[0];

      if(highestYPoint > highestPossiblePoint) {
        highestYPoint = highestPossiblePoint;
      }

      if(lowestYPoint < lowestPossiblePoint) {
        lowestYPoint = lowestPossiblePoint;
      }

      const startingPoint = selection[0];
      const endingPoint = selection[1];
      const reducerStartPoint = startingPoint % 30;
      const reducerEndingPoint = 30 - (endingPoint % 30);
      const startingZoomPointInSeconds = startingPoint - reducerStartPoint;
      const endingZoomPointInSeconds = endingPoint + reducerEndingPoint;

      if((endingZoomPointInSeconds - startingZoomPointInSeconds) >= this.xMaxSeconds) {
        lowestYPoint = this.yTicks[0];
        highestYPoint = this.yTicks[this.yTicks.length - 1];
      }

      return [lowestYPoint, highestYPoint];
    },
    formatZoomedInValues (data) {
      return this.$options.filters.secondsToMinutesSecondsReduced(data);
    },
    handleMouseOut () {
      this.tooltip.attr("transform", "translate(-999999999, -999999999)");
      this.focus.attr("transform", "translate(-999999999, -999999999)");
    },
    mouseMoveOnChart(rect) {
      let context = this;
      let pointerX = this.xScale.invert(d3.mouse(rect)[0]);
      let pointerY = this.yScale.invert(d3.mouse(rect)[1]);
      let closestDot = {};
      let minimalDifference = null;

      if(this.chartMode === 'ZOOMED_IN_MEDIUM') {
        pointerX = this.xScaleMiddleZoom.invert(d3.mouse(rect)[0]);
        pointerY = this.yScale.invert(d3.mouse(rect)[1]);

        if((this.selection[1] - this.selection[0]) < 5*60 ) {
          const dotsKeys = Object.keys(this.chartDataHighestPrecise);
          const filteredDotsByZoomedSegment = dotsKeys.filter(function(d) {
            const inScopeOfZoomedSelection = d >= context.selection[0] && d <= context.selection[1];
            return inScopeOfZoomedSelection;
          });

          const yPointDiff = (this.highestYPoint - this.lowestYPoint);
          const xPointDiff = (this.selection[1] - this.selection[0]) / 60;
          const xCloseIndex = (this.width * xPointDiff) / (this.chartHeight * yPointDiff);

          for(let i = 0; i < filteredDotsByZoomedSegment.length; i++) {
            const x = parseInt(filteredDotsByZoomedSegment[i]);
            const y = this.chartDataHighestPrecise[x];
            const xDifference = Math.abs(pointerX - x);
            const yDifference = Math.abs(pointerY - y) * xCloseIndex;
            const totalDifference = xDifference + yDifference;

            if(y <= context.y.max && y >= context.y.min) {
              if(totalDifference < minimalDifference || minimalDifference === null) {
                minimalDifference = totalDifference;
                closestDot = { x, y };
              }
            }
          }

          if(closestDot.x && closestDot.y) {
            closestDot.label = `${this.playerInfo.playerFirstName} ${this.playerInfo.playerLastName}`;
            closestDot.color = this.mainLineColor;
            this.generateTooltipByClosestDot(closestDot.x, closestDot.y, closestDot.label, closestDot.color, this.xScaleMiddleZoom, this.yScale, true);
          }
        } else if((this.selection[1] - this.selection[0]) < 15*60 ) {
          const dotsKeys = Object.keys(this.chartDataHighPrecise);
          const filteredDotsByZoomedSegment = dotsKeys.filter(function(d) {
            const inScopeOfZoomedSelection = d >= context.selection[0] && d <= context.selection[1];
            return inScopeOfZoomedSelection;
          });

          const yPointDiff = (this.highestYPoint - this.lowestYPoint);
          const xPointDiff = (this.selection[1] - this.selection[0]) / 60;
          const xCloseIndex = (this.width * xPointDiff) / (this.chartHeight * yPointDiff);

          for(let i = 0; i < filteredDotsByZoomedSegment.length; i++) {
            const x = parseInt(filteredDotsByZoomedSegment[i]);
            const y = this.chartDataHighPrecise[x];
            const xDifference = Math.abs(pointerX - x);
            const yDifference = Math.abs(pointerY - y) * xCloseIndex;
            const totalDifference = xDifference + yDifference;

            if(y <= context.y.max && y >= context.y.min) {
              if(totalDifference < minimalDifference || minimalDifference === null) {
                minimalDifference = totalDifference;
                closestDot = { x, y };
              }
            }
          }

          if(closestDot.x && closestDot.y) {
            closestDot.label = `${this.playerInfo.playerFirstName} ${this.playerInfo.playerLastName}`;
            closestDot.color = this.mainLineColor;
            this.generateTooltipByClosestDot(closestDot.x, closestDot.y, closestDot.label, closestDot.color, this.xScaleMiddleZoom, this.yScale, true);
          }
        } else {
          const dotsKeys = Object.keys(this.chartDataMediumPrecise);
          const filteredDotsByZoomedSegment = dotsKeys.filter(function(d) {
            const inScopeOfZoomedSelection = d >= context.selection[0] && d <= context.selection[1];
            return inScopeOfZoomedSelection;
          });

          const yPointDiff = (this.highestYPoint - this.lowestYPoint);
          const xPointDiff = (this.selection[1] - this.selection[0]) / 60;
          const xCloseIndex = (this.width * xPointDiff) / (this.chartHeight * yPointDiff);

          for(let i = 0; i < filteredDotsByZoomedSegment.length; i++) {
            const x = parseInt(filteredDotsByZoomedSegment[i]);
            const y = this.chartDataMediumPrecise[x];
            const xDifference = Math.abs(pointerX - x);
            const yDifference = Math.abs(pointerY - y) * xCloseIndex;
            const totalDifference = xDifference + yDifference;

            if(y <= context.y.max && y >= context.y.min) {
              if(totalDifference < minimalDifference || minimalDifference === null) {
                minimalDifference = totalDifference;
                closestDot = { x, y };
              }
            }
          }

          if(closestDot.x && closestDot.y) {
            closestDot.label = `${this.playerInfo.playerFirstName} ${this.playerInfo.playerLastName}`;
            closestDot.color = this.mainLineColor;
            this.generateTooltipByClosestDot(closestDot.x, closestDot.y, closestDot.label, closestDot.color, this.xScaleMiddleZoom, this.yScale, true);
          }
        }
      } else {
        this.dots.forEach(function(dot) {
          let xCloseIndex = context.xMaxDecimal - context.x.min;
          let yCloseIndex = context.y.max - context.y.min;
          let xDifference = Math.abs(pointerX - dot.x) * yCloseIndex;
          let yDifference = Math.abs(pointerY - dot.y) * xCloseIndex;
          let totalDifference = xDifference + yDifference;

          if(dot.y <= context.y.max && dot.y >= context.y.min) {
            if(totalDifference < minimalDifference || minimalDifference === null) {
              minimalDifference = totalDifference;
              closestDot = dot;
            }
          }
        });

        this.generateTooltipByClosestDot(closestDot.x, closestDot.y, closestDot.label, closestDot.color, this.xScale, this.yScale);
        }
      },
      textOverflowHandler (e) {
        let self = e,
          textLength = self.node().getComputedTextLength(),
          text = self.text();
        while (textLength > (this.tooltipWidth - 2 * 10) && text.length > 0) {
          text = text.slice(0, -1);
          self.text(text + '...');
          textLength = self.node().getComputedTextLength();
        }
      },
    generateTooltipByClosestDot (x, y, label, color, xScale, yScale, formattingTooltip) {
      const context = this;

      if(x && y && !this.presentationView) {
        if(this.width < (this.tooltipWidth + this.margin.left + this.margin.right)) { // responsive
          this.tooltip.attr("transform", "translate(" + 0 + "," + 0 + ")");
        } else {
          if(( this.width - xScale(x)) > this.tooltipWidth) {
            if(yScale(y) < (this.tooltipHeight + 15)) {
              this.tooltip.attr("transform", "translate(" + (xScale(x) + 10) + "," + (yScale(y)) + ")");
            } else {
              this.tooltip.attr("transform", "translate(" + (xScale(x) + 10) + "," + (yScale(y) - 100) + ")");
            }
          } else {
            if(this.yScale(y) < (this.tooltipHeight + 15)) {
              this.tooltip.attr("transform", "translate(" + (xScale(x) - 10 - this.tooltipWidth) + "," + (yScale(y)) + ")");
            } else {
              this.tooltip.attr("transform", "translate(" + (xScale(x) - 10 - this.tooltipWidth) + "," + (yScale(y) - 100) + ")");
            }
          }
        }

        this.tooltip.select(".tooltip-tspan")
          .text(function() { return label }).each(function() {
            context.textOverflowHandler.bind(this);
          })

        this.tooltip.select(".tooltip-value-y")
          .text(function() { 
            if(context.yUnit === '%') {
              return y + context.yUnit;
            }

            // MET units are of type double
            if(context.yUnit === 'MET' || context.yUnit === UNIT_LABELS[UNIT_SYSTEM.IMPERIAL]){
              return Number(y).toFixed(2) + ' ' + context.yUnit;
            }

            return context.yUnit ? ( Math.round(y) + ' ' + context.yUnit ) : Math.round(y);
          });

        this.tooltip.select(".tooltip-value-x")
          .text(function() {
            let seconds;
            let minutes;

            if(!formattingTooltip) {
              if((x * 60) > context.xMaxSeconds) {
                minutes = Math.floor(context.xMaxSeconds / 60);
                seconds = context.xMaxSeconds - (minutes * 60);
                return minutes + ' min' + ' ' + seconds + ' sec';
              }
            } else {
              if(x > context.xMaxSeconds) {
                minutes = Math.floor(context.xMaxSeconds / 60);
                seconds = context.xMaxSeconds - (minutes * 60);
                return minutes + ' min' + ' ' + seconds + ' sec';
              } else {
                minutes = Math.floor(x / 60);
                seconds = x - (minutes * 60);

                if(seconds !== 0) {
                  return minutes + ' min' + ' ' + seconds + ' sec';
                } else {
                  return minutes + ' min';
                }
              }
            }

            return context.xUnit ? ( x + ' ' + context.xUnit ) : x;
          });

        this.focus.attr("transform", "translate(" + xScale(x) + "," + yScale(y) + ")");

        this.focus.select("circle")
          .attr("fill", color);

        this.focus.select(".x-hover-line")
          .attr("stroke", color)
          .attr("y2", this.chartHeight - yScale(y));

        this.focus.select(".y-hover-line")
          .attr("stroke", color)
          .attr("x2", this.width + this.width);

        this.focus.style("display", null); 
        this.tooltip.style("display", null);
      }
    },
    generateBrushes () {
      const context = this;

      // Brushes
      if(this.displayBrushes && window.innerWidth > 768 && !this.presentationView) {
        this.gBrushes = this.main.append("g").attr("class", "brushes");
        this.brushes = [];
        this.mySelections = {};

        this.gBrushes
          .on("mousemove", function() {
            context.mouseMoveOnChart(this);
          })
          .on("mouseout", function() {
            context.handleMouseOut(this);
          });
      }
    },
    newBrush () {
      const context = this;
      let brush = d3
        .brushX()
        .extent([[0, 0], [this.width, this.chartHeight]])
        .on("end", brushend.bind(context));

      this.brushes.push({ id: this.brushes.length, brush: brush });

      function brushend() {
        // Figure out if our latest brush has a selection
        let selection;

        if(this.chartMode === 'ZOOMED_IN_MEDIUM') {
          selection = d3.event.selection.map(function(i) {
            return context.xScaleMiddleZoom.invert(i) ;
          });

          selection = [selection[0] / 60, selection[1] / 60];
        } else {
          selection = d3.event.selection.map(function(i) {
            return context.xScale.invert(i) ;
          });
        }

        // Figure out if our latest brush has a selection
        let lastBrushID = this.brushes[this.brushes.length - 1].id;

        this.mySelections[lastBrushID] = { start: selection[0], end: selection[1] };
        
        if(this.onBrushCreation) {
          this.onBrushCreation({ selection: this.mySelections[lastBrushID] });
        }
      }
    },
    updateChartWidth () {
      let newWidth;
      if (this.$refs.lineChart != undefined) {
        newWidth = this.$refs.lineChart.parentElement.offsetWidth - this.margin.right;
        newWidth = newWidth - 15;
      }
      
      let dynamicXScale;

      this.defaultXScale.range([0, newWidth]);
      this.xScaleMiddleZoom.range([0, newWidth]);

      if(this.chartMode === 'DEFAULT') { 
        dynamicXScale = this.defaultXScale;
      } else {
        dynamicXScale = this.xScaleMiddleZoom;
      }

      if(newWidth > 0) {
        this.width = newWidth;

        if(this.displayBrushes && window.innerWidth > 768) {
          this.gBrushes.selectAll('.brush').remove();
          this.newBrush();
          lineChartService.drawBrushes(this.gBrushes, this.brushes);
        }

        this.main
          .selectAll('.main-search-scope rect')
          .attr('width', newWidth)
          .attr("fill", 'transparent')
          .attr('height', this.chartHeight)

        this.svg.attr('width', newWidth + this.margin.left + this.margin.right);

        this.clipPathRect
          .attr('width', newWidth);

        this.clipPreviewRect
          .attr('width', newWidth);

        this.clipXaxisRect
          .attr('width', newWidth + 30);

        this.xGridPathRect
          .attr('width', newWidth);

        this.xScalePreviewChart
          .domain([this.x.min, this.xMaxDecimal]) // input
          .range([0, newWidth]); // output

        dynamicXScale
          .domain([this.x.min, this.xMaxDecimal]) // input
          .range([0, newWidth]); // output

        this.svg.select('#clipXaxis rect')
          .attr('width', newWidth + this.margin.right);

        this.xAxis = d3
          .axisBottom(dynamicXScale)
          .tickValues(this.xTicks);

        this.xAxisMiddleZoom = d3
          .axisBottom(dynamicXScale)
          .tickValues(this.xTicksMediumZoom)
          .tickFormat(this.formatZoomedInValues);

        this.svg
          .select('.x.axis')
          .attr("transform", "translate(0," + this.chartHeight + ")")
          .call(this.xAxis);

        this.svg
          .select('.axis.axis--x')
          .attr("transform", "translate(0," + this.zoomPreviewHeight + ")")
          .call(this.xAxis);

        this.svg
          .select('.y.axis')
          .call(d3.axisLeft(this.yScale));

        this.svg.select('.grid.grid-x')
          .attr("transform", "translate(0," + this.chartHeight + ")")
          .call(
            d3
              .axisBottom(dynamicXScale)
              .ticks(this.xTicks.length)
              .tickValues(this.xTicks)
              .tickSize(-this.chartHeight)
              .tickFormat("")
          );

        this.svg.select('.grid.grid-y')
          .call(
            d3
              .axisLeft(this.yScale)
              .ticks(this.yTicks.length)
              .tickValues(this.yTicks)
              .tickSize(-newWidth)
              .tickFormat("")
          );

        this.svg.select('.axis-label.axis-label-x')
          .attr("x", newWidth / 2);

        const zoomRange = [];
        if (!this.presentationView) {
          zoomRange[0] = ((this.selection[0]) / this.xMaxSeconds) * newWidth;
          zoomRange[1] = ((this.selection[1]) / this.xMaxSeconds) * newWidth;
        }

        this.zoomBrush
          .extent([[0, 0], [newWidth, this.zoomPreviewHeight]]);
        
        this.zBrush
          .call(this.zoomBrush)
          .call(this.zoomBrush.move, zoomRange);

        this.zoomContext
          .call(this.zoomBrush)
          .call(this.zoomBrush.move, zoomRange);

        this.handle
          .attr('d', this.brushResizePath);

        this.zoomContext
          .selectAll(".line.zoom-preview-line")
          .attr('d', this.previewLine);

        this.main
          .selectAll(".line.path.medium-precise-path")
          .attr('d', this.mediumZoomline);

        this.main
          .selectAll(".line.path.high-precise-path")
          .attr('d', this.mediumZoomline);

        this.main
          .selectAll(".line.path.highest-precise-path")
          .attr('d', this.mediumZoomline);
      }
    },
    segmentLabelOverflowHandler(e) {
      let self = d3.select(e),
          textLength = self.node().getComputedTextLength(),
          text = self.text();

      while (textLength > (this.chartHeight - 2 * 10) && text.length > 0) {
          text = text.slice(0, -1);
          self.text(text + '...');
          textLength = self.node().getComputedTextLength();
      }
    }
  }
}
</script>

<style lang="scss">
  .custom-line-chart {
    margin-bottom: 20px;
    overflow: hidden;

    .axis {
      color: #5E5E62;
      font-size: 14px;
    }

    .line {
      fill: none;
      stroke: #F68D2E;
      stroke-width: 1;
    }

    .line.main {
      stroke-width: 5;
    }

    .axis-label {
      color: #fff;
      font-family: sans-serif;
      font-size: 14px;
    }

    .overlay-rect {
      fill: none;
      pointer-events: all;
    }
    
    .hover-line {
      stroke-width: 2px;
      stroke-dasharray: 3,3;
      display: none;
    }

    /* Style the dots by assigning a fill and stroke */
    .dot {
      stroke: #000000;
      stroke-opacity: .3;
    }

    .grid line {
      stroke: lightgrey;
      stroke-opacity: 0.7;
      shape-rendering: crispEdges;
    }

    .grid path {
      stroke-width: 0;
    }

    .grid .tick line {
      stroke: #2f2f2f;
    }

    .overlay {
      fill: none;
      pointer-events: all;
    }

    .focus circle {
      stroke-width: 1px;
      stroke: #000000;
      stroke-opacity: .3;
    }

    .brushes {
      .brush .selection {
        fill: #5E5E62;
        stroke: #5E5E62;
        stroke-width: 1px;
        stroke-dasharray: 3px 3px;
      }
    }

    .zoom-brush {
      .selection {
        stroke: $elephant-gray;
      }

      &:hover {
        .selection {
          stroke: $elephant-gray;
        }

        .handle--custom {
          stroke: $elephant-gray;
        }
      }
    } 
  }
</style>
